import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import "../../../../components/blog/editor.css";
import { throwIfNotOk } from "@/lib/api-error";
import { blogListParams } from "@/components/blog/blog-list";
import { Plus } from "@phosphor-icons/react";
import { BlogPostCard } from "@/components/admin/blog/BlogPostCard";
import { BlogPreviewDialog } from "@/components/admin/blog/BlogPreviewDialog";
import { BlogRejectDialog } from "@/components/admin/blog/BlogRejectDialog";
import { BlogDeleteDialog } from "@/components/admin/blog/BlogDeleteDialog";
import { BlogFilterTabs } from "@/components/admin/blog/BlogFilterTabs";
import { BlogPagination } from "@/components/admin/blog/BlogPagination";
import { BlogEmptyState } from "@/components/admin/blog/BlogEmptyState";
import { BlogSkeletonCard } from "@/components/admin/blog/BlogSkeletonCard";
import type { BlogStatusFilter } from "@/components/admin/blog/BlogFilterTabs";
import type { BlogPost } from "@/types";

export const Route = createFileRoute("/dashboard/admin/blog/")({
  component: AdminBlog,
});

const POSTS_PER_PAGE = 10;

function AdminBlog() {
  const queryClient = useQueryClient();
  const {
    page,
    setPage,
    searchQuery,
    debouncedSearch,
    setSearch: handleSearchChange,
    filters,
    setFilter,
    pageSize,
  } = usePagination<{ statusFilter: BlogStatusFilter }>(
    { pageSize: POSTS_PER_PAGE },
    { statusFilter: "pending" },
  );
  const { statusFilter } = filters;
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "blog", statusFilter, page, debouncedSearch],
    queryFn: async () => {
      const params = blogListParams({
        page,
        limit: pageSize,
        status: statusFilter,
        q: debouncedSearch,
      });
      const res = await fetch(`/api/blog?${params}`);
      if (!res.ok) throw new Error("Failed to fetch blog posts");
      return res.json() as Promise<{
        data: BlogPost[];
        total: number;
        page: number;
      }>;
    },
    placeholderData: (prev) => prev,
  });

  const posts = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / POSTS_PER_PAGE);

  const statusMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      rejectionReason,
    }: {
      id: string;
      status: "published" | "rejected";
      rejectionReason?: string;
    }) => {
      const res = await fetch(`/api/blog/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, rejectionReason }),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju statusa članka");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "blog"] });
      queryClient.invalidateQueries({ queryKey: ["blog"] });
      setRejectingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/blog/${id}/update`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju članka");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "blog"] });
      queryClient.invalidateQueries({ queryKey: ["blog"] });
      setDeletingId(null);
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1
            className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Blog članci
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Upravljajte blog člancima i odobravajte sadržaj terapeuta.
          </p>
        </div>
        <Button asChild className="shrink-0">
          <Link to="/dashboard/admin/blog/new">
            <Plus className="size-4" />
            Novi članak
          </Link>
        </Button>
      </div>

      {/* Tabs + search */}
      <BlogFilterTabs
        statusFilter={statusFilter}
        searchQuery={searchQuery}
        onStatusChange={(v) => setFilter("statusFilter", v)}
        onSearchChange={handleSearchChange}
      />

      {/* Posts */}
      {isLoading && posts.length === 0 ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <BlogSkeletonCard key={i} />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <BlogEmptyState
          status={statusFilter}
          hasSearch={debouncedSearch.length > 0}
        />
      ) : (
        <>
          <div className="space-y-3">
            {posts.map((post) => (
              <BlogPostCard
                key={post.id}
                post={post}
                onPreview={() => setPreviewPost(post)}
                onReject={() => setRejectingId(post.id)}
                onApprove={() =>
                  statusMutation.mutate({ id: post.id, status: "published" })
                }
                onDelete={() => setDeletingId(post.id)}
                isApproving={statusMutation.isPending}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <BlogPagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={POSTS_PER_PAGE}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Preview dialog */}
      {previewPost && (
        <BlogPreviewDialog
          post={previewPost}
          onClose={() => setPreviewPost(null)}
          onReject={(id) => {
            setPreviewPost(null);
            setRejectingId(id);
          }}
          onApprove={(id) => {
            statusMutation.mutate({ id, status: "published" });
            setPreviewPost(null);
          }}
        />
      )}

      {/* Reject dialog */}
      {rejectingId && (
        <BlogRejectDialog
          isPending={statusMutation.isPending}
          onConfirm={(reason) =>
            statusMutation.mutate({
              id: rejectingId,
              status: "rejected",
              rejectionReason: reason,
            })
          }
          onClose={() => setRejectingId(null)}
        />
      )}

      {/* Delete confirmation */}
      {deletingId && (
        <BlogDeleteDialog
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deletingId)}
          onClose={() => setDeletingId(null)}
        />
      )}
    </div>
  );
}
